import * as React from "react";
import { connect } from "react-redux";
import { notification } from "antd";
import { idTableColumn } from "../../app/components/AntTableStyles";
import { FoCard, FoTable } from "./financeUi";
import { getCashCycles, getPayoutsDue } from "../../app/store/actions/whFinanceActions";
import { useEffect } from "react";
import moment from "moment";
import download from "downloadjs";
import ExcelJS from 'exceljs';
import axios from "axios";

export const DuePayouts = ({
  getPayoutsDue,
  user,
  token,
  transactions,
  getCashCycles,
  cashCycles,
  summary,
  isLoading
}) => {
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(10);
  const [sortField, setSortField] = React.useState('');
  const [sortOrder, setSortOrder] = React.useState('');

  useEffect(() => {
    const params = {
      page,
      limit,
      ...(sortField && sortOrder && { sortOrder: sortOrder === 'ascend' ? '1' : '-1' })
    };
    getPayoutsDue(params);
    getCashCycles({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, sortField, sortOrder])

  const downloadPayoutsCSV = () => {
    let content = "vendor_id,amount\n"
    summary?.docs?.forEach(({ vendor, totalDue }) => {
      // Include positive (payouts due) and negative (outstanding collections) balances
      if (totalDue !== 0) {
        content = content + `${vendor._id},${totalDue.toFixed(2)}\n`
      }
    });
    download(content, `duePayouts_${moment().format("DD-MM-YYYY_HH_mm")}.csv`)
  }

  const handleExportToExcel = async () => {
    setIsDownloading(true);
    const response = await axios.get(`${process.env.REACT_APP_API_BASE}/Finance/payouts/due/export`, {
        headers: {
            "x-access-token": token
        }
    });
    const allSummary = response.data.summary;
    notification.info({ message: "Downloading Payout Export" })
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Due Payouts');
    workbook.creator = 'Chainz';

    // Submissions by Country
    worksheet.columns = [
      { header: 'Vendor Id', key: 'vendorId' },
      { header: 'Vendor Name', key: 'vendorName' },
      { header: 'Total Paid', key: 'totalDue' },
    ];

    allSummary?.docs?.map(({ vendor, totalDue }) => {
      worksheet.addRow({
        "vendorId": vendor._id,
        "vendorName": vendor.name,
        "totalDue": totalDue.toFixed(2),
      });
    });

    worksheet.autoFilter = `A1:C${(summary?.docs?.length || 0) + 1}`;
    const buffer = await workbook.xlsx.writeBuffer();
    const mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    const fileName = `${moment().format("DD_MM_YYYY")} Due Payouts.xlsx`
    const blob = new Blob([buffer], { type: mimeType })
    download(blob, fileName, mimeType);
    setIsDownloading(false)
  }

  const payoutColumns = [
    idTableColumn({ title: 'Vendor ID' }),
    {
      title: 'Vendor',
      dataIndex: ['vendor', 'name'],
    },
    {
      title: 'Total Income',
      dataIndex: 'totalIncome',
      render: (_, { totalIncome }) => `${totalIncome} EGP`,
    },
    {
      title: 'Total Fees',
      dataIndex: 'totalFees',
      render: (_, { totalFees }) => `${totalFees} EGP`,
    },
    {
      title: 'Total On Hold',
      dataIndex: 'totalHold',
      render: (_, { totalHold }) => `${totalHold} EGP`,
    },
    {
      title: 'Total Paid',
      dataIndex: 'totalPayout',
      render: (_, { totalPayout }) => `${totalPayout} EGP`,
    },
    {
      title: 'Total Due',
      dataIndex: 'totalDue',
      defaultSortOrder: 'descend',
      render: (_, { totalDue }) => (
        <span style={{ fontWeight: 600, color: totalDue < 0 ? "var(--r)" : "var(--tx)" }}>
          {totalDue} EGP
        </span>
      ),
      sorter: true,
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    setLimit(pagination.pageSize);
    
    // Handle sorting
    if (sorter && sorter.field) {
      setSortField(sorter.field);
      setSortOrder(sorter.order);
    } else {
      setSortField('');
      setSortOrder('');
    }
  };

  const payoutData = summary?.docs?.map((row) => {
    return {
      _id: row.vendor._id,
      vendor: row.vendor,
      totalIncome: parseFloat(row.totalIncome.toFixed(2)),
      totalFees: parseFloat(row.totalFees.toFixed(2)),
      totalHold: parseFloat(row.totalHold.toFixed(2)),
      totalPayout: parseFloat(row.totalPayout.toFixed(2)),
      totalDue: parseFloat(row.totalDue.toFixed(2))
    }
  }) || []

  return (
    <FoCard
      title="Due payout balances"
      count={`${summary?.totalDocs || 0} vendors`}
      note="Wallet totals from income, fees, holds, and payouts. Dated due, overdue, and reminder actions are not on this API yet."
      actions={
        <button type="button" className="fo-btn" onClick={() => handleExportToExcel()} disabled={isDownloading}>
          {isDownloading ? "Exporting..." : "Export"}
        </button>
      }
    >
      <FoTable
        columns={payoutColumns}
        dataSource={payoutData}
        loading={isLoading}
        rowKey="_id"
        onChange={handleTableChange}
        empty="No vendor balances."
        pagination={{
          current: page,
          pageSize: limit,
          total: summary?.totalDocs || 0,
        }}
      />
    </FoCard>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isLoading: state?.records?.isLoading,
  token: state?.auth?.token,
  summary: state?.records?.summary,
  cashCycles: state?.records?.cashCycles
});

const mapDispatchToProps = { getPayoutsDue, getCashCycles };

export default connect(mapStateToProps, mapDispatchToProps)(DuePayouts);
